import { getPersistedValue } from 'shared/utils/persisted-values';
import { EventTracker, Events } from '../events';

const HIGHLIGHT_CSS = 'font-weight: bold; color: #FF3E51; background-color: #153F66; padding: 2px 4px;';

export const createConsoleLogTracker = (): EventTracker => {
  const doLog = !!getPersistedValue('analytics:consoleLogTracker');

  const maybeLog = (method: keyof EventTracker): ((...args: any[]) => void) => (payload: unknown, context: unknown) => {
    if (doLog) {
      console.groupCollapsed(`%cDUTCHIE_ANALYTICS%c   %s`, HIGHLIGHT_CSS, '', method);
      console.info(`%cDUTCHIE_ANALYTICS%c PAYLOAD %o`, HIGHLIGHT_CSS, '', payload);
      console.info(`%cDUTCHIE_ANALYTICS%c CONTEXT %o`, HIGHLIGHT_CSS, '', context);
      console.groupEnd();
    }
  };

  const logStoreRouting = (payload: Events['storeRouting']): void => {
    if (!doLog) {
      return;
    }

    const commonPayload = {
      source: payload.source,
      originDispensaryId: payload.originDispensaryId,
      ...(payload.destinationDispensaryId !== undefined && {
        destinationDispensaryId: payload.destinationDispensaryId,
      }),
      ...(payload.cartHadItems !== undefined && { cartHadItems: payload.cartHadItems }),
    };
    const safePayload: Events['storeRouting'] =
      payload.action === 'failed'
        ? { ...commonPayload, action: 'failed', failureReason: payload.failureReason }
        : { ...commonPayload, action: payload.action };
    console.groupCollapsed(`%cDUTCHIE_ANALYTICS%c   %s`, HIGHLIGHT_CSS, '', 'storeRouting');
    console.info(`%cDUTCHIE_ANALYTICS%c PAYLOAD %o`, HIGHLIGHT_CSS, '', safePayload);
    console.groupEnd();
  };

  return {
    onContextChange: (update, newContext, oldContext) => {
      if (doLog) {
        console.groupCollapsed(
          `%cDUTCHIE_ANALYTICS%c   %s`,
          HIGHLIGHT_CSS,
          'color: #929394;',
          `onContextChange [${Object.keys(update).join(', ')}]`
        );
        console.info(`%cDUTCHIE_ANALYTICS%c UPDATE %o`, HIGHLIGHT_CSS, '', update);
        console.info(`%cDUTCHIE_ANALYTICS%c NEW CONTEXT %o`, HIGHLIGHT_CSS, '', newContext);
        console.info(`%cDUTCHIE_ANALYTICS%c OLD CONTEXT %o`, HIGHLIGHT_CSS, '', oldContext);
        console.groupEnd();
      }
    },
    accountLogin: maybeLog('accountLogin'),
    addedProductToCart: maybeLog('addedProductToCart'),
    buyItAgainImpression: maybeLog('buyItAgainImpression'),
    buyItAgainViewAllClicked: maybeLog('buyItAgainViewAllClicked'),
    checkoutStarted: maybeLog('checkoutStarted'),
    dutchiePayEnrollmentButtonClicked: maybeLog('dutchiePayEnrollmentButtonClicked'),
    dutchiePayCTAButtonClicked: maybeLog('dutchiePayCTAButtonClicked'),
    dutchiePayRelinkButtonViewed: maybeLog('dutchiePayRelinkButtonViewed'),
    dutchiePayRelinkButtonClicked: maybeLog('dutchiePayRelinkButtonClicked'),
    dutchiePayUnlinkRemoveCtaClicked: maybeLog('dutchiePayUnlinkRemoveCtaClicked'),
    dutchiePayUnlinkModalViewed: maybeLog('dutchiePayUnlinkModalViewed'),
    dutchiePayUnlinkConfirmed: maybeLog('dutchiePayUnlinkConfirmed'),
    dutchiePayUnlinkCancelled: maybeLog('dutchiePayUnlinkCancelled'),
    dutchiePayUnlinkSuccess: maybeLog('dutchiePayUnlinkSuccess'),
    dutchiePayUnlinkError: maybeLog('dutchiePayUnlinkError'),
    dutchiePayUnenrollment: maybeLog('dutchiePayUnenrollment'),
    dutchiePayEnrollmentStep: maybeLog('dutchiePayEnrollmentStep'),
    dutchiePayEnrollmentStepCancelled: maybeLog('dutchiePayEnrollmentStepCancelled'),
    dutchiePayPlaidOnEvent: maybeLog('dutchiePayPlaidOnEvent'),
    dutchiePayPlaidOnLoad: maybeLog('dutchiePayPlaidOnLoad'),
    dutchiePayServiceFeeShown: maybeLog('dutchiePayServiceFeeShown'),
    dutchiePayIncentiveExposure: maybeLog('dutchiePayIncentiveExposure'),
    dutchiePayPaymentMethodSelected: maybeLog('dutchiePayPaymentMethodSelected'),
    dutchiePayPlaidOnSuccess: maybeLog('dutchiePayPlaidOnSuccess'),
    dutchiePayPlaidOnExit: maybeLog('dutchiePayPlaidOnExit'),
    dutchiePayAeroSyncOnEvent: maybeLog('dutchiePayAeroSyncOnEvent'),
    dutchiePayAeroSyncOnLoad: maybeLog('dutchiePayAeroSyncOnLoad'),
    dutchiePayAeroSyncOnClose: maybeLog('dutchiePayAeroSyncOnClose'),
    dutchiePayAeroSyncOnSuccess: maybeLog('dutchiePayAeroSyncOnSuccess'),
    dutchiePayAeroSyncOnError: maybeLog('dutchiePayAeroSyncOnError'),
    dutchiePayPreAuthEvent: maybeLog('dutchiePayPreAuthEvent'),
    dutchiePayInstoreAccountLinkViewed: maybeLog('dutchiePayInstoreAccountLinkViewed'),
    dutchiePayInstoreLoginViewed: maybeLog('dutchiePayInstoreLoginViewed'),
    dutchiePayInstoreCartApprovalClicked: maybeLog('dutchiePayInstoreCartApprovalClicked'),
    dutchiePayInstoreCartApprovalFailed: maybeLog('dutchiePayInstoreCartApprovalFailed'),
    dutchiePayInstoreCartApprovalSucceeded: maybeLog('dutchiePayInstoreCartApprovalSucceeded'),
    dutchiePayInstoreCartViewed: maybeLog('dutchiePayInstoreCartViewed'),
    loyaltySignup: maybeLog('loyaltySignup'),
    paymentsError: maybeLog('paymentsError'),
    placedOrder: maybeLog('placedOrder'),
    privacyPreferenceUpdated: maybeLog('privacyPreferenceUpdated'),
    productClicked: maybeLog('productClicked'),
    removedProductFromCart: maybeLog('removedProductFromCart'),
    searchedForProduct: maybeLog('searchedForProduct'),
    searchResults: maybeLog('searchResults'),
    viewedCheckout: maybeLog('viewedCheckout'),
    checkoutSession: maybeLog('checkoutSession'),
    viewedProduct: maybeLog('viewedProduct'),
    cartUpdated: maybeLog('cartUpdated'),
    cartViewed: maybeLog('cartViewed'),
    storeRouting: logStoreRouting,
    imageBannerClicked: maybeLog('imageBannerClicked'),
    sponsoredProductImpression: maybeLog('sponsoredProductImpression'),
    sponsoredProductClicked: maybeLog('sponsoredProductClicked'),
    sponsoredBannerImpression: maybeLog('sponsoredBannerImpression'),
    sponsoredBannerClicked: maybeLog('sponsoredBannerClicked'),
    gaGTMClickEvent: maybeLog('gaGTMClickEvent'),
    dispensaryClicked: maybeLog('dispensaryClicked'),
    dutchiePayKioskQRCodeModalViewed: maybeLog('dutchiePayKioskQRCodeModalViewed'),
    viewedKioskCheckoutModal: maybeLog('viewedKioskCheckoutModal'),
    loginNudge: maybeLog('loginNudge'),
    loginGateShown: maybeLog('loginGateShown'),
    loginEntryPointClicked: maybeLog('loginEntryPointClicked'),
    dutchiePayCheckoutFlowAction: maybeLog('dutchiePayCheckoutFlowAction'),
    roundupForCharityViewed: maybeLog('roundupForCharityViewed'),
    roundupForCharityToggled: maybeLog('roundupForCharityToggled'),
    walletPass: maybeLog('walletPass'),
    dutchiePayLinkToPayInitiated: maybeLog('dutchiePayLinkToPayInitiated'),
    dutchiePayLinkToPayIneligible: maybeLog('dutchiePayLinkToPayIneligible'),
    dutchiePayEcommModalPaymentSwitchViewed: maybeLog('dutchiePayEcommModalPaymentSwitchViewed'),
    dutchiePayEcommModalPaymentSwitchClicked: maybeLog('dutchiePayEcommModalPaymentSwitchClicked'),
    dutchiePayEcommModalPaymentSwitchOutcome: maybeLog('dutchiePayEcommModalPaymentSwitchOutcome'),
    dutchiePayLoyaltyCtaClicked: maybeLog('dutchiePayLoyaltyCtaClicked'),
    productRatingViewed: maybeLog('productRatingViewed'),
    productReviewsViewed: maybeLog('productReviewsViewed'),
    productReviewAiSummaryViewed: maybeLog('productReviewAiSummaryViewed'),
    productReviewStatusChecked: maybeLog('productReviewStatusChecked'),
    productReviewCtaClicked: maybeLog('productReviewCtaClicked'),
    productReviewSubmitted: maybeLog('productReviewSubmitted'),
    instoreEnrollmentStepViewed: maybeLog('instoreEnrollmentStepViewed'),
    instoreEnrollmentStepCompleted: maybeLog('instoreEnrollmentStepCompleted'),
    instoreEnrollmentStepSkipped: maybeLog('instoreEnrollmentStepSkipped'),
    medCardSignupCompleted: maybeLog('medCardSignupCompleted'),
    medCardSignupFailed: maybeLog('medCardSignupFailed'),
    accountSignupAction: maybeLog('accountSignupAction'),
    loyaltyEnrollmentAction: maybeLog('loyaltyEnrollmentAction'),
    phoneVerificationNudgeAction: maybeLog('phoneVerificationNudgeAction'),
    consumerPulseGoogleReviewAction: maybeLog('consumerPulseGoogleReviewAction'),
    onboardingModalStep: maybeLog('onboardingModalStep'),
    onboardingFlowStarted: maybeLog('onboardingFlowStarted'),
    conciergeEntryPointClicked: maybeLog('conciergeEntryPointClicked'),
    conciergeImpression: maybeLog('conciergeImpression'),
    conciergeAction: maybeLog('conciergeAction'),
    conciergeMessageSent: maybeLog('conciergeMessageSent'),
    conciergeExited: maybeLog('conciergeExited'),
    conciergeBundleAdded: maybeLog('conciergeBundleAdded'),
    conciergeAccessGate: maybeLog('conciergeAccessGate'),
    conciergeConversationRefreshed: maybeLog('conciergeConversationRefreshed'),
    conciergeMessageStopped: maybeLog('conciergeMessageStopped'),
    conciergeError: maybeLog('conciergeError'),
    conciergeIdleWarning: maybeLog('conciergeIdleWarning'),
    conciergeSessionLimitReached: maybeLog('conciergeSessionLimitReached'),
  };
};
